import React, { useEffect, useRef } from 'react';
import './App.css';
import axios from 'axios';

// Components
import PaymentModal from './PaymentModal';
import PhotoCarousel from './components/common/PhotoCarousel';
import LoginScreen from './components/auth/LoginScreen';
import SideNavigation from './components/layout/SideNavigation';
import UnsubscribeModal from './components/modals/UnsubscribeModal';
import FeedView from './components/feed/FeedView';
import CreateReviewView from './components/creator/CreateReviewView';
import CreatorsListView from './components/creator/CreatorsListView';
import CreatorProfileView from './components/creator/CreatorProfileView';
import SavedView from './components/saved/SavedView';
import ProfileView from './components/profile/ProfileView';
import ManageSubscriptionsView from './components/subscriptions/ManageSubscriptionsView';

// Stores (Client State)
import {
  useAuthStore,
  useUIStore,
  useReviewFormStore,
  useProfileFormStore,
} from './shared/stores';

// Hooks (Server State)
import {
  useCurrentUser,
  useEmergentAuth,
  useBecomeCreator,
  useFeed,
  useCreators,
  useSavedRestaurants,
  useSaveReview,
  useUnsaveReview,
  useSubscriptions,
  useSubscribe,
  useUnsubscribe,
  useCreatorProfile,
  useMyReviews,
  useMySubscribers,
  useUpdateProfile,
  useCreateReview,
} from './shared/hooks';

import { API_URL } from './utils/constants';

function App() {
  // ========== AUTH STORE ==========
  const { token, user, setToken, setUser, logout } = useAuthStore();

  // ========== UI STORE ==========
  const {
    currentView,
    setCurrentView,
    showCarousel,
    carouselReview,
    carouselIndex,
    openCarousel,
    closeCarousel,
    showPaymentModal,
    paymentData,
    openPaymentModal,
    closePaymentModal,
    showUnsubscribeModal,
    unsubscribeData,
    openUnsubscribeModal,
    closeUnsubscribeModal,
    showToast,
    toastMessage,
    showToastMessage,
    selectedCreatorId,
    reviewFilter,
    setSelectedCreatorId,
    setReviewFilter,
  } = useUIStore();

  // ========== FORM STORES ==========
  const reviewForm = useReviewFormStore();
  const profileForm = useProfileFormStore();

  // ========== SERVER STATE QUERIES ==========
  const { data: currentUser, isLoading: isLoadingUser, refetch: refetchUser } = useCurrentUser(token);
  const { data: feed = [] } = useFeed(!!token && currentView === 'feed');
  const { data: creators = [] } = useCreators(!!token && currentView === 'creators');
  // Always fetch saved restaurants when authenticated (needed to show save state in all views)
  const { data: savedRestaurants = [] } = useSavedRestaurants(!!token);
  const { data: subscriptions = [] } = useSubscriptions(!!token);
  const { data: myReviews = [] } = useMyReviews(!!token && currentView === 'profile');
  const { data: mySubscribers = [] } = useMySubscribers(!!token && currentView === 'profile');

  // Creator Profile (conditional)
  const {
    data: creatorProfileData,
    refetch: refetchCreatorProfile,
  } = useCreatorProfile(selectedCreatorId, currentView === 'creatorProfile');

  const creatorProfile = creatorProfileData?.profile;
  const creatorReviews = creatorProfileData?.reviews || [];

  // ========== MUTATIONS ==========
  const handleAuthSuccess = React.useCallback((data) => {
    console.log('[Auth Debug] ✅ Auth success handler called!');
    console.log('[Auth Debug] Response data:', data);
    console.log('[Auth Debug] Has data?', !!data);
    console.log('[Auth Debug] Has token?', !!data?.token);
    
    if (data && data.token) {
      console.log('[Auth Debug] ✅ Condition passed - has data and token');
      console.log('[Auth Debug] Token received, length:', data.token.length);
      console.log('[Auth Debug] Token value:', data.token.substring(0, 20) + '...');
      console.log('[Auth Debug] Setting token in store...');
      
      setToken(data.token);
      console.log('[Auth Debug] Token set complete');
      
      if (data.user) {
        console.log('[Auth Debug] Setting user data:', data.user.email);
        setUser(data.user);
        console.log('[Auth Debug] User set complete');
      }
      
      console.log('[Auth Debug] About to redirect to homepage in 100ms...');
      setTimeout(() => {
        console.log('[Auth Debug] Executing redirect NOW...');
        window.location.href = '/';
      }, 100);
    } else {
      console.error('[Auth Debug] ❌ Condition failed - no data or token!');
      console.error('[Auth Debug] data:', data);
      console.error('[Auth Debug] data.token:', data?.token);
      showToastMessage('Authentication error: No token received.');
    }
  }, [setToken, setUser, showToastMessage]);

  const emergentAuthMutation = useEmergentAuth(handleAuthSuccess);
  const becomeCreatorMutation = useBecomeCreator();
  const saveMutation = useSaveReview();
  const unsaveMutation = useUnsaveReview();
  const subscribeMutation = useSubscribe();
  const unsubscribeMutation = useUnsubscribe();
  const updateProfileMutation = useUpdateProfile();
  const createReviewMutation = useCreateReview();

  // ========== REFS ==========
  const isProcessingAuth = useRef(false);
  const hasInitiatedAuth = useRef(false);

  // ========== DERIVED STATE ==========
  const savedReviewIds = new Set(savedRestaurants.map((s) => s.review.review_id));
  const monthlyRevenue = mySubscribers
    .filter((s) => s.tier === 'premium')
    .reduce((sum, s) => sum + (s.creator_subscription_price || 0), 0);

  // ========== EFFECTS ==========
  // Set user when current user data is fetched
  useEffect(() => {
    if (currentUser) {
      setUser(currentUser);
    }
  }, [currentUser, setUser]);

  // Handle Emergent Auth Callback
  useEffect(() => {
    const currentPath = window.location.pathname;
    
    // Extract session from query params OR hash fragment
    const urlParams = new URLSearchParams(window.location.search);
    let sessionId = urlParams.get('session');
    
    // If not in query params, check hash fragment
    if (!sessionId && window.location.hash) {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      sessionId = hashParams.get('session_id') || hashParams.get('session');
    }
    
    console.log('[Auth Debug] Checking for session...', { 
      path: currentPath,
      sessionId, 
      hasToken: !!token,
      hasInitiated: hasInitiatedAuth.current
    });
    
    // Handle /auth/callback route - only initiate once
    if (currentPath === '/auth/callback' && sessionId && !token && !hasInitiatedAuth.current) {
      console.log('[Auth Debug] Auth callback route detected, exchanging session...');
      console.log('[Auth Debug] Session ID to exchange:', sessionId);
      hasInitiatedAuth.current = true;
      emergentAuthMutation.mutate(sessionId);
    }
    
    // Handle errors
    if (emergentAuthMutation.isError) {
      console.error('[Auth Debug] ❌ Mutation failed!');
      console.error('[Auth Debug] Error:', emergentAuthMutation.error);
      showToastMessage('Authentication failed. Please try again.');
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
    }
  }, [token, emergentAuthMutation, showToastMessage]);

  // ========== HANDLERS ==========
  const handleGoogleLogin = async () => {
    console.log('[Auth Debug] handleGoogleLogin called');
    console.log('[Auth Debug] API_URL:', API_URL);
    try {
      console.log('[Auth Debug] Fetching auth URL from backend...');
      const response = await axios.get(`${API_URL}/api/auth/google`, {
        timeout: 15000, // 15 second timeout
      });
      console.log('[Auth Debug] Backend response:', response.data);
      if (response.data.auth_url) {
        console.log('[Auth Debug] Redirecting to:', response.data.auth_url);
        window.location.href = response.data.auth_url;
      } else {
        console.error('[Auth Debug] No auth_url in response');
        showToastMessage('Login configuration error. Please contact support.');
      }
    } catch (error) {
      console.error('[Auth Debug] Login error:', error);
      console.error('[Auth Debug] Error details:', error.response?.data || error.message);
      showToastMessage('Login failed. Please try again.');
    }
  };

  const handleLogout = () => {
    logout();
    setCurrentView('feed');
  };

  const handleBecomeCreator = async () => {
    try {
      await becomeCreatorMutation.mutateAsync();
      showToastMessage('You are now a creator! 🎉');
      refetchUser();
    } catch (error) {
      console.error('Become creator error:', error);
      showToastMessage('Failed to become creator. Please try again.');
    }
  };

  const handleSaveReview = async (reviewId, restaurantName) => {
    try {
      await saveMutation.mutateAsync(reviewId);
      showToastMessage(`Saved ${restaurantName}! 💚`);
    } catch (error) {
      console.error('Save review error:', error);
      showToastMessage('Failed to save review.');
    }
  };

  const handleUnsaveReview = async (reviewId, restaurantName) => {
    try {
      await unsaveMutation.mutateAsync(reviewId);
      showToastMessage(`Removed ${restaurantName} from saved`);
    } catch (error) {
      console.error('Unsave review error:', error);
      showToastMessage('Failed to remove review.');
    }
  };

  const handleSubscribe = async (creatorId, tier = 'free') => {
    try {
      await subscribeMutation.mutateAsync({ creatorId, tier });
      showToastMessage('Subscribed successfully! 🎉');
    } catch (error) {
      console.error('Subscribe error:', error);
      showToastMessage('Failed to subscribe. Please try again.');
    }
  };

  const handleConfirmUnsubscribe = async () => {
    if (!unsubscribeData) return;

    try {
      await unsubscribeMutation.mutateAsync(unsubscribeData.subscriptionId);
      showToastMessage(`Unsubscribed from ${unsubscribeData.creatorName}`);
      closeUnsubscribeModal();
    } catch (error) {
      console.error('Unsubscribe error:', error);
      showToastMessage('Failed to unsubscribe. Please try again.');
      closeUnsubscribeModal();
    }
  };

  const handleViewCreatorProfile = (creatorId) => {
    setSelectedCreatorId(creatorId);
    setCurrentView('creatorProfile');
    setReviewFilter('all');
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        reviewForm.addPhotos([reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleRemovePhoto = (index) => {
    reviewForm.removePhoto(index);
  };

  const handleSelectPhoto = (index) => {
    reviewForm.setSelectedPhotoIndex(index);
  };

  const handleDishChange = (photoIndex, field, value) => {
    reviewForm.updateDish(photoIndex, field, value);
  };

  const handleSubmitReview = async () => {
    const formData = reviewForm.getFormData();

    // Validate required fields
    if (!formData.restaurant?.name || !formData.restaurant?.location || !formData.description) {
      showToastMessage('Please fill in all required fields');
      return;
    }

    try {
      await createReviewMutation.mutateAsync(formData);
      showToastMessage('Review posted successfully! 🎉');
      reviewForm.resetForm();
      setCurrentView('profile');
    } catch (error) {
      console.error('Submit review error:', error);
      showToastMessage('Failed to post review. Please try again.');
    }
  };

  const handleEditProfile = () => {
    profileForm.initializeForm(user);
    profileForm.setIsEditingProfile(true);
  };

  const handleSaveProfile = async () => {
    const formData = profileForm.getFormData();

    try {
      await updateProfileMutation.mutateAsync(formData);
      showToastMessage('Profile updated successfully! ✨');
      profileForm.setIsEditingProfile(false);
      refetchUser();
    } catch (error) {
      console.error('Update profile error:', error);
      showToastMessage('Failed to update profile. Please try again.');
    }
  };

  const handlePictureUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        profileForm.setEditPicture(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePaymentSuccess = async () => {
    if (!paymentData) return;

    try {
      await subscribeMutation.mutateAsync({
        creatorId: paymentData.creatorId,
        tier: 'premium',
      });
      showToastMessage(`Subscribed to ${paymentData.creatorName} (Premium)! 🎉`);
      closePaymentModal();
      
      // Refetch creator profile if viewing
      if (currentView === 'creatorProfile' && selectedCreatorId === paymentData.creatorId) {
        refetchCreatorProfile();
      }
    } catch (error) {
      console.error('Payment success handler error:', error);
      showToastMessage('Subscription activated but there was an error. Please refresh.');
      closePaymentModal();
    }
  };

  // ========== LOGIN SCREEN ==========
  console.log('[Auth Debug] Render check:', { hasToken: !!token, hasUser: !!user, isLoadingUser, currentUser });
  
  if (!token) {
    console.log('[Auth Debug] No token - showing login screen');
    return <LoginScreen onGoogleLogin={handleGoogleLogin} />;
  }

  // Show loading while fetching user data
  if (token && !user && isLoadingUser) {
    console.log('[Auth Debug] Has token, loading user...');
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your profile...</p>
        </div>
      </div>
    );
  }

  // If we have token but no user and not loading, something went wrong
  if (token && !user && !isLoadingUser) {
    console.log('[Auth Debug] Token exists but failed to load user - showing login');
    return <LoginScreen onGoogleLogin={handleGoogleLogin} />;
  }
  
  console.log('[Auth Debug] All checks passed - showing main app');

  // ========== MAIN APP ==========
  return (
    <div className="min-h-screen bg-background flex">
      {/* Side Navigation */}
      <SideNavigation
        user={user}
        currentView={currentView}
        onViewChange={setCurrentView}
        onBecomeCreator={handleBecomeCreator}
      />

      {/* Main Content Area */}
      <div className="flex-1 ml-20">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
          <div className="max-w-mobile mx-auto px-6 py-4 flex items-center justify-between">
            <h1 className="text-xl font-heading font-bold text-primary">
              {currentView === 'feed' && 'Feed'}
              {currentView === 'creators' && 'Discover Creators'}
              {currentView === 'create' && 'Create Review'}
              {currentView === 'saved' && 'Saved'}
              {currentView === 'profile' && 'Profile'}
              {currentView === 'creatorProfile' && 'Creator Profile'}
              {currentView === 'manageSubscriptions' && 'Manage Subscriptions'}
            </h1>
          </div>
        </header>

        {/* Content */}
        <div className="max-w-mobile mx-auto px-6 py-6">
          {/* FEED VIEW */}
          {currentView === 'feed' && (
            <FeedView
              feed={feed}
              savedReviewIds={savedReviewIds}
              onViewCreators={() => setCurrentView('creators')}
              onSaveReview={handleSaveReview}
              onUnsaveReview={handleUnsaveReview}
              onViewCreator={handleViewCreatorProfile}
              onUpgrade={openPaymentModal}
              onPhotoClick={openCarousel}
            />
          )}

          {/* CREATE REVIEW VIEW */}
          {currentView === 'create' && (
            <CreateReviewView
              restaurantName={reviewForm.restaurantName}
              setRestaurantName={reviewForm.setRestaurantName}
              restaurantLocation={reviewForm.restaurantLocation}
              setRestaurantLocation={reviewForm.setRestaurantLocation}
              cuisineTypes={reviewForm.cuisineTypes}
              setCuisineTypes={reviewForm.setCuisineTypes}
              description={reviewForm.description}
              setDescription={reviewForm.setDescription}
              rating={reviewForm.rating}
              setRating={reviewForm.setRating}
              insiderTips={reviewForm.insiderTips}
              setInsiderTips={reviewForm.setInsiderTips}
              visibility={reviewForm.visibility}
              setVisibility={reviewForm.setVisibility}
              photos={reviewForm.photos}
              dishes={reviewForm.dishes}
              selectedPhotoIndex={reviewForm.selectedPhotoIndex}
              setSelectedPhotoIndex={reviewForm.setSelectedPhotoIndex}
              loading={createReviewMutation.isPending}
              onPhotoUpload={handlePhotoUpload}
              onRemovePhoto={handleRemovePhoto}
              onSelectPhoto={handleSelectPhoto}
              onDishChange={handleDishChange}
              onSubmitReview={handleSubmitReview}
            />
          )}

          {/* DISCOVER VIEW */}
          {currentView === 'creators' && (
            <CreatorsListView
              creators={creators}
              currentUserId={user.user_id}
              subscriptions={subscriptions}
              loading={subscribeMutation.isPending}
              onViewCreator={handleViewCreatorProfile}
              onSubscribe={handleSubscribe}
              onUnsubscribe={openUnsubscribeModal}
            />
          )}

          {/* SAVED VIEW */}
          {currentView === 'saved' && (
            <SavedView
              savedRestaurants={savedRestaurants}
              onPhotoClick={openCarousel}
              onSaveReview={handleSaveReview}
              onUnsaveReview={handleUnsaveReview}
              onViewCreator={handleViewCreatorProfile}
              onUpgrade={openPaymentModal}
            />
          )}

          {/* CREATOR PROFILE VIEW */}
          {currentView === 'creatorProfile' && creatorProfile && (
            <CreatorProfileView
              creatorProfile={creatorProfile}
              creatorReviews={creatorReviews}
              reviewFilter={reviewFilter}
              setReviewFilter={setReviewFilter}
              subscriptions={subscriptions}
              savedReviewIds={savedReviewIds}
              onBack={() => setCurrentView('feed')}
              onSubscribe={handleSubscribe}
              onUnsubscribe={openUnsubscribeModal}
              onUpgrade={openPaymentModal}
              onPhotoClick={openCarousel}
              onSaveReview={handleSaveReview}
              onUnsaveReview={handleUnsaveReview}
              onViewCreator={handleViewCreatorProfile}
            />
          )}

          {/* PROFILE VIEW */}
          {currentView === 'profile' && (
            <ProfileView
              user={user}
              isEditingProfile={profileForm.isEditingProfile}
              editName={profileForm.editName}
              setEditName={profileForm.setEditName}
              editBio={profileForm.editBio}
              setEditBio={profileForm.setEditBio}
              editDescription={profileForm.editDescription}
              setEditDescription={profileForm.setEditDescription}
              editPrice={profileForm.editPrice}
              setEditPrice={profileForm.setEditPrice}
              editPicture={profileForm.editPicture}
              subscriptions={subscriptions}
              mySubscribers={mySubscribers}
              monthlyRevenue={monthlyRevenue}
              myReviews={myReviews}
              savedReviewIds={savedReviewIds}
              loading={updateProfileMutation.isPending}
              onEditProfile={handleEditProfile}
              onSaveProfile={handleSaveProfile}
              onCancelEdit={() => profileForm.setIsEditingProfile(false)}
              onPictureUpload={handlePictureUpload}
              onLogout={handleLogout}
              onManageSubscriptions={() => setCurrentView('manageSubscriptions')}
              onCreateReview={() => setCurrentView('create')}
              onPhotoClick={openCarousel}
              onSaveReview={handleSaveReview}
              onUnsaveReview={handleUnsaveReview}
              onViewCreator={handleViewCreatorProfile}
              onUpgrade={openPaymentModal}
            />
          )}

          {/* MANAGE SUBSCRIPTIONS VIEW */}
          {currentView === 'manageSubscriptions' && (
            <ManageSubscriptionsView
              subscriptions={subscriptions}
              onBack={() => setCurrentView('profile')}
              onDiscoverCreators={() => setCurrentView('creators')}
              onUpgrade={openPaymentModal}
              onSwitchToFree={(creatorName) => {
                showToastMessage(`Switched ${creatorName} to free tier`);
              }}
              onUnsubscribe={openUnsubscribeModal}
            />
          )}
        </div>
      </div>

      {/* Toast Notification */}
      {showToast && (
        <div className="fixed top-24 left-1/2 transform -translate-x-1/2 bg-primary text-white px-6 py-3 rounded-full shadow-lg z-50 animate-slide-down" data-testid="toast-notification">
          {toastMessage}
        </div>
      )}

      {/* Photo Carousel Modal */}
      {showCarousel && carouselReview && (
        <PhotoCarousel
          review={carouselReview}
          initialIndex={carouselIndex}
          onClose={closeCarousel}
        />
      )}

      {/* Payment Modal */}
      {showPaymentModal && paymentData && (
        <PaymentModal
          creatorId={paymentData.creatorId}
          creatorName={paymentData.creatorName}
          amount={paymentData.amount}
          onSuccess={handlePaymentSuccess}
          onClose={closePaymentModal}
          token={token}
        />
      )}

      {/* Unsubscribe Confirmation Modal */}
      <UnsubscribeModal
        isOpen={showUnsubscribeModal && !!unsubscribeData}
        creatorName={unsubscribeData?.creatorName}
        onConfirm={handleConfirmUnsubscribe}
        onCancel={closeUnsubscribeModal}
      />
    </div>
  );
}

export default App;
